import { Component, inject, signal, computed } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { DataModelSchema, DataModelNode, DataModelVariant, isMultiVariant } from '../../models/data-model.model';
import { DataModelTreeNodeComponent } from './data-model-tree-node.component';

export interface DataModelDialogData {
  schema: DataModelSchema;
}

@Component({
  selector: 'app-data-model-dialog',
  imports: [
    MatIcon,
    FormsModule,
    TranslocoPipe,
    DataModelTreeNodeComponent
  ],
  templateUrl: './data-model-dialog.component.html',
  styleUrl: './data-model-dialog.component.scss'
})
export class DataModelDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<DataModelDialogComponent>);
  readonly data: DataModelDialogData = inject(MAT_DIALOG_DATA);

  readonly hasVariants = isMultiVariant(this.data.schema);
  readonly variants: DataModelVariant[] = isMultiVariant(this.data.schema) ? this.data.schema.variants : [];

  activeVariantIndex = signal(0);
  searchTerm = signal('');

  activeRoot = computed<DataModelNode>(() => {
    const schema = this.data.schema;
    if (isMultiVariant(schema)) {
      return schema.variants[this.activeVariantIndex()].root;
    }
    return schema.root;
  });

  activeVariantDescription = computed<string | undefined>(() => {
    if (!this.hasVariants) return undefined;
    return this.variants[this.activeVariantIndex()]?.description;
  });

  expandedNodes = signal<Set<string>>(new Set([this.activeRoot().name]));

  totalAttributes = computed(() => this.countAttributes(this.activeRoot()));

  allExpanded = false;

  close(): void {
    this.dialogRef.close();
  }

  selectVariant(index: number): void {
    if (index === this.activeVariantIndex()) return;
    this.activeVariantIndex.set(index);
    this.searchTerm.set('');
    this.expandedNodes.set(new Set([this.activeRoot().name]));
    this.allExpanded = false;
  }

  onSearchInput(term: string): void {
    this.searchTerm.set(term.trim());
    if (term.trim()) {
      this.expandAllNodes();
    } else {
      this.expandedNodes.set(new Set([this.activeRoot().name]));
    }
  }

  expandAll(): void {
    this.expandAllNodes();
    this.allExpanded = true;
  }

  collapseAll(): void {
    this.expandedNodes.set(new Set([this.activeRoot().name]));
    this.allExpanded = false;
  }

  onToggleExpand(path: string): void {
    const current = new Set(this.expandedNodes());
    if (current.has(path)) {
      current.delete(path);
    } else {
      current.add(path);
    }
    this.expandedNodes.set(current);
  }

  private expandAllNodes(): void {
    const expanded = new Set<string>();
    this.collectExpandablePaths(this.activeRoot(), '', expanded);
    this.expandedNodes.set(expanded);
  }

  private collectExpandablePaths(node: DataModelNode, parentPath: string, set: Set<string>): void {
    const path = parentPath ? `${parentPath}.${node.name}` : node.name;
    if (node.children?.length) {
      set.add(path);
      node.children.forEach(child => this.collectExpandablePaths(child, path, set));
    }
  }

  matchesAny(): boolean {
    return this.nodeMatchesSearch(this.activeRoot(), this.searchTerm());
  }

  private nodeMatchesSearch(node: DataModelNode, term: string): boolean {
    if (!term) return true;
    const t = term.toLowerCase();
    if (node.name.toLowerCase().includes(t)) return true;
    if (node.type.toLowerCase().includes(t)) return true;
    if (node.example !== undefined && String(node.example).toLowerCase().includes(t)) return true;
    if (node.description?.toLowerCase().includes(t)) return true;
    if (node.children) {
      return node.children.some(c => this.nodeMatchesSearch(c, t));
    }
    return false;
  }

  private countAttributes(node: DataModelNode): number {
    let count = 0;
    if (node.children) {
      for (const child of node.children) {
        count++;
        count += this.countAttributes(child);
      }
    }
    return count;
  }
}
